import { Theme, WithTheme, cx } from '@mint/core';
import styled, { keyframes } from 'styled-components';
import React from 'react';
import { SpinnerProps } from './spinner-props';

const ROOT_CLASS_NAME = 'spinner';

const DEFAULT_STROKE_WIDTH = 5;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

export const SpinnerComponent = React.forwardRef<any, SpinnerProps>(
  (props, ref): React.ReactElement => {
    const { className, strokeWidth = DEFAULT_STROKE_WIDTH, ...rest } = props;

    const componentProps = {
      className: cx(ROOT_CLASS_NAME, className),
      ...rest,
      ref,
    };

    return (
      <div {...componentProps}>
        <svg viewBox="0 0 50 50">
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth={strokeWidth}
          ></circle>
        </svg>
      </div>
    );
  },
);

SpinnerComponent.displayName = 'Spinner';

const resolveColor = (
  color: SpinnerProps['color'],
  theme: Theme,
): [string, string] => {
  switch (color) {
    case 'error':
      return [theme.palette.error.main, theme.vars.palette.error.main];
    case 'secondary':
      return [theme.palette.secondary.main, theme.vars.palette.secondary.main];
    case 'warning':
      return [theme.palette.warning.main, theme.vars.palette.warning.main];
    case 'neutral':
      return [theme.palette.neutral[500], theme.vars.palette.neutral[500]];
    case 'primary':
    default:
      return [theme.palette.primary.main, theme.vars.palette.primary.main];
  }
};

const interpolateStroke = (props: WithTheme<SpinnerProps>): string => {
  const [color, colorVar] = resolveColor(props.color, props.theme);
  return `
    stroke: ${color};
    stroke: var(${colorVar}, ${color});
  `;
};

export const Spinner = styled(SpinnerComponent)`
  display: inline-block;
  width: 1em;

  &,
  & svg {
    line-height: inherit;
  }

  svg {
    animation: ${rotate} 2s linear infinite;
    top: 0.17em;
    position: relative;

    circle {
      ${interpolateStroke}
      stroke-linecap: round;
      animation: ${dash} 1.5s ease-in-out infinite;
    }
  }
`;
